import React, { useState, Component } from 'react';
import { RotateCcw, Package, Clock, CheckCircle2, XCircle, CircleDot, ChevronRight, Plus, X, Check, Camera, Upload, AlertTriangle, ArrowLeft } from 'lucide-react';
import { ClientReturn, ReturnReason } from './types';
type Props = {
  returns: ClientReturn[];
  onAddReturn: (r: ClientReturn) => void;
};
const STATUS_CONFIG: Record<ClientReturn['status'], {
  bg: string;
  text: string;
  border: string;
  icon: ComponentType<any>;
}> = {
  'In attesa': {
    bg: 'bg-[#F5A524]/10',
    text: 'text-[#F5A524]',
    border: 'border-[#F5A524]/30',
    icon: Clock
  },
  Approvato: {
    bg: 'bg-[#5DA9FF]/10',
    text: 'text-[#5DA9FF]',
    border: 'border-[#5DA9FF]/30',
    icon: CircleDot
  },
  Concluso: {
    bg: 'bg-[#D4FF3A]/10',
    text: 'text-[#D4FF3A]',
    border: 'border-[#D4FF3A]/30',
    icon: CheckCircle2
  },
  Rifiutato: {
    bg: 'bg-[#EF4444]/10',
    text: 'text-[#EF4444]',
    border: 'border-[#EF4444]/30',
    icon: XCircle
  }
};
// Lightweight set of delivered shipments available for return creation
type DeliveredShipment = {
  id: string;
  trackingId: string;
  destination: string;
  carrier: string;
  deliveredOn: string;
  items: {
    id: string;
    name: string;
    sku: string;
    quantity: number;
  }[];
};
const DELIVERED_SHIPMENTS: DeliveredShipment[] = [{
  id: 'SH-2840-116',
  trackingId: 'UPS771203',
  destination: 'Via Etnea 102, Catania',
  carrier: 'UPS',
  deliveredOn: '08 mag',
  items: [{
    id: 'i1',
    name: 'Costume mare uomo',
    sku: 'CM-UM-M-BL',
    quantity: 4
  }, {
    id: 'i2',
    name: 'Pareo donna',
    sku: 'PA-DN-UN-FL',
    quantity: 3
  }]
}, {
  id: 'SH-2840-112',
  trackingId: 'GLS664102',
  destination: 'Corso Buenos Aires 21, Milano',
  carrier: 'GLS',
  deliveredOn: '05 mag',
  items: [{
    id: 'i1',
    name: 'Giacca tecnica nera',
    sku: 'GT-NR-M',
    quantity: 2
  }, {
    id: 'i2',
    name: 'Cintura uomo cuoio',
    sku: 'CN-UM-CU-S',
    quantity: 3
  }]
}, {
  id: 'SH-2840-108',
  trackingId: 'FX5512098',
  destination: 'Piazza Garibaldi 4, Napoli',
  carrier: 'FedEx',
  deliveredOn: '02 mag',
  items: [{
    id: 'i1',
    name: 'Maglione lana grigio',
    sku: 'ML-WL-GR-L',
    quantity: 5
  }]
}];
const REASONS: ReturnReason[] = ['Difettoso / danneggiato', 'Capo errato', 'Taglia o colore sbagliato', 'Non corrisponde alla descrizione', 'Danneggiato in transito', 'Altro'];
export function ClientReturns({
  returns,
  onAddReturn
}: Props) {
  const [creating, setCreating] = useState(false);
  const sorted = [...returns].sort((a, b) => a.createdAt < b.createdAt ? 1 : -1);
  return <div className="space-y-6" data-id="element-168">
      <div className="flex items-end justify-between flex-wrap gap-3" data-id="element-169">
        <div data-id="element-170">
          <h1 className="text-2xl font-semibold text-[#FAFAFA] font-heading tracking-tight" data-id="element-171">
            Resi
          </h1>
        </div>
        <button type="button" onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#D4FF3A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#c2ed28] transition" data-id="element-172">
          <Plus className="h-4 w-4" data-id="element-173" />
          Nuovo reso
        </button>
      </div>

      {returns.length === 0 ? <div className="bg-[#181818] border border-[#262626] rounded-xl p-12 text-center" data-id="element-174">
          <div className="h-12 w-12 rounded-xl bg-[#0A0A0A] border border-[#262626] mx-auto flex items-center justify-center mb-4" data-id="element-175">
            <RotateCcw className="h-5 w-5 text-[#6B6B6B]" data-id="element-176" />
          </div>
          <h3 className="text-base font-semibold text-[#FAFAFA] font-heading mb-1" data-id="element-177">
            Nessun reso attivo
          </h3>
          <p className="text-sm text-[#9A9A9A] max-w-sm mx-auto mb-5" data-id="element-178">
            Quando aprirai una richiesta di reso da una spedizione consegnata,
            la troverai qui con tutti i dettagli e lo stato di gestione.
          </p>
          <button type="button" onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#D4FF3A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#c2ed28] transition" data-id="element-179">
            <Plus className="h-4 w-4" data-id="element-180" />
            Apri il primo reso
          </button>
        </div> : <div className="bg-[#181818] border border-[#262626] rounded-xl overflow-hidden" data-id="element-181">
          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-[#1F1F1F]" data-id="element-182">
            {sorted.map(r => {
          const cfg = STATUS_CONFIG[r.status];
          const Icon = cfg.icon;
          return <div key={r.id} className="p-4" data-id="element-183">
                  <div className="flex items-start justify-between gap-3" data-id="element-184">
                    <div className="min-w-0" data-id="element-185">
                      <div className="text-sm font-mono text-[#FAFAFA]" data-id="element-186">
                        {r.id}
                      </div>
                      <div className="text-xs text-[#9A9A9A] mt-1" data-id="element-187">
                        {r.reason}
                      </div>
                      <div className="text-[11px] text-[#6B6B6B] font-mono mt-1 flex items-center gap-1.5" data-id="element-188">
                        <Package className="h-3 w-3" data-id="element-189" />
                        {r.shipmentId} · {r.items.length} capi · {r.createdAt}
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${cfg.bg} ${cfg.text} ${cfg.border} shrink-0`} data-id="element-190">
                      <Icon className="h-3 w-3" data-id="element-191" />
                      {r.status}
                    </span>
                  </div>
                </div>;
        })}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto" data-id="element-192">
            <table className="w-full text-sm [&_th]:whitespace-nowrap [&_td]:whitespace-nowrap" data-id="element-193">
              <thead data-id="element-194">
                <tr className="text-left text-[10px] uppercase tracking-wider text-[#6B6B6B] border-b border-[#1F1F1F]" data-id="element-195">
                  <th className="px-5 py-3 font-medium" data-id="element-196">ID reso</th>
                  <th className="px-5 py-3 font-medium" data-id="element-197">Spedizione</th>
                  <th className="px-5 py-3 font-medium" data-id="element-198">Motivo</th>
                  <th className="px-5 py-3 font-medium text-center" data-id="element-199">N. capi</th>
                  <th className="px-5 py-3 font-medium" data-id="element-200">Data</th>
                  <th className="px-5 py-3 font-medium" data-id="element-201">Stato</th>
                  <th className="px-5 py-3 font-medium w-8" data-id="element-202" />
                </tr>
              </thead>
              <tbody data-id="element-203">
                {sorted.map(r => {
              const cfg = STATUS_CONFIG[r.status];
              const Icon = cfg.icon;
              return <tr key={r.id} className="border-b border-[#1F1F1F] last:border-0 hover:bg-[#0A0A0A] transition-colors cursor-pointer" data-id="element-204">
                      <td className="px-5 py-3.5 font-mono text-[#FAFAFA]" data-id="element-205">
                        {r.id}
                      </td>
                      <td className="px-5 py-3.5 font-mono text-xs text-[#9A9A9A]" data-id="element-206">
                        {r.shipmentId}
                      </td>
                      <td className="px-5 py-3.5 text-[#9A9A9A] max-w-[280px] truncate" data-id="element-207">
                        {r.reason}
                      </td>
                      <td className="px-5 py-3.5 text-center font-mono text-[#FAFAFA]" data-id="element-208">
                        {r.items.length}
                      </td>
                      <td className="px-5 py-3.5 font-mono text-xs text-[#9A9A9A]" data-id="element-209">
                        {r.createdAt}
                      </td>
                      <td className="px-5 py-3.5" data-id="element-210">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${cfg.bg} ${cfg.text} ${cfg.border}`} data-id="element-211">
                          <Icon className="h-3 w-3" data-id="element-212" />
                          {r.status}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-right" data-id="element-213">
                        <ChevronRight className="h-4 w-4 text-[#6B6B6B] inline" data-id="element-214" />
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>
        </div>}

      {creating && <NewReturnDrawer shipments={DELIVERED_SHIPMENTS} existingReturns={returns} onClose={() => setCreating(false)} onSubmit={payload => {
      onAddReturn(payload);
      setCreating(false);
    }} data-id="element-215" />}
    </div>;
}
function NewReturnDrawer({
  shipments,
  existingReturns,
  onClose,
  onSubmit
}: {
  shipments: DeliveredShipment[];
  existingReturns: ClientReturn[];
  onClose: () => void;
  onSubmit: (r: ClientReturn) => void;
}) {
  const [step, setStep] = useState<'select-shipment' | 'fill-form' | 'success'>('select-shipment');
  const [selectedShipment, setSelectedShipment] = useState<DeliveredShipment | null>(null);
  const [selectedItems, setSelectedItems] = useState<Record<string, boolean>>({});
  const [reason, setReason] = useState<ReturnReason | ''>('');
  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState<string[]>([]);
  const usedShipmentIds = new Set(existingReturns.map(r => r.shipmentId));
  const availableShipments = shipments.filter(s => !usedShipmentIds.has(s.id));
  const selectedCount = Object.values(selectedItems).filter(Boolean).length;
  const canSubmit = !!reason && selectedCount > 0 && !!selectedShipment;
  const handleSubmit = () => {
    if (!canSubmit || !selectedShipment) return;
    const payload: ClientReturn = {
      id: `RT-${Math.floor(10000 + Math.random() * 90000)}`,
      shipmentId: selectedShipment.id,
      createdAt: new Date().toISOString().slice(0, 10),
      reason: reason as ReturnReason,
      description,
      items: selectedShipment.items.filter(i => selectedItems[i.id]).map(i => ({
        id: i.id,
        name: i.name,
        sku: i.sku
      })),
      status: 'In attesa'
    };
    onSubmit(payload);
    setStep('success');
  };
  return <div className="fixed inset-0 z-50 flex items-stretch justify-end" data-id="element-216">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} data-id="element-217" />
      <div className="relative w-full sm:max-w-lg bg-[#0A0A0A] border-l border-[#262626] flex flex-col" data-id="element-218">
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-[#262626]" data-id="element-219">
          <div className="min-w-0 flex items-center gap-2.5" data-id="element-220">
            {step === 'fill-form' && <button type="button" onClick={() => setStep('select-shipment')} className="p-1 -ml-1 rounded text-[#9A9A9A] hover:text-[#FAFAFA] hover:bg-[#181818] transition" aria-label="Indietro" data-id="element-221">
                <ArrowLeft className="h-4 w-4" data-id="element-222" />
              </button>}
            <div className="min-w-0" data-id="element-223">
              <h3 className="text-base font-semibold text-[#FAFAFA] font-heading" data-id="element-224">
                {step === 'success' ? 'Richiesta inviata' : 'Nuovo reso'}
              </h3>
              <div className="text-xs text-[#9A9A9A] mt-0.5" data-id="element-225">
                {step === 'select-shipment' && 'Step 1 di 2 · Seleziona la spedizione'}
                {step === 'fill-form' && selectedShipment && <>
                    Step 2 di 2 · Spedizione{' '}
                    <span className="font-mono text-[#FAFAFA]" data-id="element-226">
                      {selectedShipment.id}
                    </span>
                  </>}
              </div>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded text-[#9A9A9A] hover:text-[#FAFAFA] hover:bg-[#181818] transition" aria-label="Chiudi" data-id="element-227">
            <X className="h-4 w-4" data-id="element-228" />
          </button>
        </div>

        {step === 'select-shipment' && <>
            <div className="flex-1 overflow-y-auto p-5 space-y-3" data-id="element-229">
              <div className="text-[11px] uppercase tracking-wider text-[#9A9A9A] font-medium" data-id="element-230">
                Spedizioni consegnate disponibili
              </div>
              {availableShipments.length === 0 ? <div className="text-sm text-[#6B6B6B] text-center py-10" data-id="element-231">
                  Tutte le spedizioni consegnate hanno già un reso aperto.
                </div> : <ul className="space-y-2" data-id="element-232">
                  {availableShipments.map(s => <li key={s.id} data-id="element-233">
                      <button type="button" onClick={() => {
                setSelectedShipment(s);
                setSelectedItems({});
                setStep('fill-form');
              }} className="w-full text-left p-3 rounded-lg bg-[#181818] border border-[#262626] hover:border-[#333] transition flex items-center justify-between gap-3" data-id="element-234">
                        <div className="min-w-0" data-id="element-235">
                          <div className="flex items-center gap-2 flex-wrap" data-id="element-236">
                            <span className="text-sm font-mono text-[#FAFAFA]" data-id="element-237">
                              {s.id}
                            </span>
                            <span className="text-[11px] text-[#6B6B6B]" data-id="element-238">
                              {s.carrier}
                            </span>
                          </div>
                          <div className="text-xs text-[#9A9A9A] mt-1 truncate" data-id="element-239">
                            {s.destination}
                          </div>
                          <div className="text-[11px] text-[#6B6B6B] mt-0.5" data-id="element-240">
                            Consegnata {s.deliveredOn} · {s.items.length} SKU
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-[#6B6B6B] shrink-0" data-id="element-241" />
                      </button>
                    </li>)}
                </ul>}
            </div>
          </>}

        {step === 'fill-form' && selectedShipment && <>
            <div className="flex-1 overflow-y-auto p-5 space-y-5" data-id="element-242">
              <div data-id="element-243">
                <div className="text-[11px] uppercase tracking-wider text-[#9A9A9A] font-medium mb-2" data-id="element-244">
                  Capi da rendere
                </div>
                <ul className="space-y-2" data-id="element-245">
                  {selectedShipment.items.map(item => {
                const checked = !!selectedItems[item.id];
                return <li key={item.id} data-id="element-246">
                        <label className={`flex items-center justify-between gap-3 p-3 rounded-lg border cursor-pointer transition ${checked ? 'bg-[#D4FF3A]/5 border-[#D4FF3A]/40' : 'bg-[#181818] border-[#262626] hover:border-[#333]'}`} data-id="element-247">
                          <div className="flex items-center gap-3 min-w-0" data-id="element-248">
                            <span className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${checked ? 'bg-[#D4FF3A] border-[#D4FF3A]' : 'bg-[#0A0A0A] border-[#333]'}`} data-id="element-249">
                              {checked && <Check className="h-3 w-3 text-[#0A0A0A]" data-id="element-250" />}
                            </span>
                            <div className="min-w-0" data-id="element-251">
                              <div className="text-sm text-[#FAFAFA] truncate" data-id="element-252">
                                {item.name}
                              </div>
                              <div className="text-[11px] font-mono text-[#6B6B6B]" data-id="element-253">
                                {item.sku} · ×{item.quantity}
                              </div>
                            </div>
                          </div>
                          <input type="checkbox" className="sr-only" checked={checked} onChange={e => setSelectedItems(s => ({
                      ...s,
                      [item.id]: e.target.checked
                    }))} data-id="element-254" />
                        </label>
                      </li>;
              })}
                </ul>
              </div>

              <div data-id="element-255">
                <div className="text-[11px] uppercase tracking-wider text-[#9A9A9A] font-medium mb-2" data-id="element-256">
                  Motivo del reso <span className="text-[#D4FF3A]" data-id="element-257">*</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-258">
                  {REASONS.map(r => {
                const active = reason === r;
                return <button key={r} type="button" onClick={() => setReason(r)} className={`text-left px-3 py-2 rounded-lg border text-xs transition ${active ? 'bg-[#D4FF3A]/5 border-[#D4FF3A]/40 text-[#FAFAFA]' : 'bg-[#181818] border-[#262626] text-[#9A9A9A] hover:border-[#333]'}`} data-id="element-259">
                        {r}
                      </button>;
              })}
                </div>
              </div>

              <div data-id="element-260">
                <div className="text-[11px] uppercase tracking-wider text-[#9A9A9A] font-medium mb-2" data-id="element-261">
                  Descrizione del problema
                </div>
                <textarea value={description} onChange={e => setDescription(e.target.value)} rows={4} placeholder="Descrivi cosa non va con i capi..." className="w-full bg-[#181818] border border-[#262626] rounded-lg px-3 py-2 text-sm text-[#FAFAFA] placeholder:text-[#6B6B6B] focus:outline-none focus:border-[#D4FF3A]/40 resize-none" data-id="element-262" />
              </div>

              <div data-id="element-263">
                <div className="text-[11px] uppercase tracking-wider text-[#9A9A9A] font-medium mb-2" data-id="element-264">
                  Allegati (facoltativo)
                </div>
                <div className="grid grid-cols-3 gap-2" data-id="element-265">
                  {[0, 1, 2].map(i => {
                const attached = !!attachments[i];
                return <button key={i} type="button" onClick={() => setAttachments(a => {
                  const next = [...a];
                  next[i] = next[i] ? '' : `IMG_${1000 + i}.jpg`;
                  return next.filter(Boolean);
                })} className={`aspect-square rounded-lg flex flex-col items-center justify-center gap-1 transition border ${attached ? 'bg-[#D4FF3A]/5 border-[#D4FF3A]/40 text-[#D4FF3A]' : 'bg-[#181818] border-dashed border-[#262626] hover:border-[#D4FF3A]/40 text-[#6B6B6B] hover:text-[#9A9A9A]'}`} data-id="element-266">
                        {attached ? <Check className="h-4 w-4" data-id="element-267" /> : i === 0 ? <Camera className="h-4 w-4" data-id="element-268" /> : <Upload className="h-4 w-4" data-id="element-269" />}
                        <span className="text-[10px]" data-id="element-270">
                          {attached ? 'Allegato' : 'Aggiungi'}
                        </span>
                      </button>;
              })}
                </div>
              </div>

              <div className="bg-[#181818] border border-[#262626] rounded-lg p-3 flex items-start gap-2.5" data-id="element-271">
                <AlertTriangle className="h-4 w-4 text-[#F5A524] shrink-0 mt-0.5" data-id="element-272" />
                <div className="text-[11px] text-[#9A9A9A] leading-relaxed" data-id="element-273">
                  La richiesta verrà presa in carico entro 48 ore lavorative.
                  Conserva i capi nella confezione originale fino alla conferma.
                </div>
              </div>
            </div>

            <div className="border-t border-[#262626] px-5 py-3 flex items-center justify-between gap-2" data-id="element-274">
              <div className="text-xs text-[#9A9A9A]" data-id="element-275">
                {selectedCount > 0 ? `${selectedCount} ${selectedCount === 1 ? 'capo' : 'capi'} · ${attachments.length} allegati` : 'Seleziona almeno un capo'}
              </div>
              <div className="flex items-center gap-2" data-id="element-276">
                <button type="button" onClick={onClose} className="px-3.5 py-2 rounded-lg bg-[#181818] border border-[#262626] text-[#FAFAFA] text-sm font-medium hover:border-[#333] transition" data-id="element-277">
                  Annulla
                </button>
                <button type="button" onClick={handleSubmit} disabled={!canSubmit} className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold transition ${canSubmit ? 'bg-[#D4FF3A] text-[#0A0A0A] hover:bg-[#c2ed28]' : 'bg-[#1F1F1F] text-[#6B6B6B] cursor-not-allowed'}`} data-id="element-278">
                  Invia richiesta
                </button>
              </div>
            </div>
          </>}

        {step === 'success' && <div className="flex-1 p-6 flex flex-col items-center justify-center text-center" data-id="element-279">
            <div className="h-16 w-16 rounded-full bg-[#D4FF3A]/10 border border-[#D4FF3A]/30 flex items-center justify-center mb-4" data-id="element-280">
              <CheckCircle2 className="h-8 w-8 text-[#D4FF3A]" data-id="element-281" />
            </div>
            <h4 className="text-base font-semibold text-[#FAFAFA] font-heading" data-id="element-282">
              Richiesta inviata
            </h4>
            <p className="text-sm text-[#9A9A9A] mt-1 max-w-xs" data-id="element-283">
              Riceverai una conferma via email entro 48 ore lavorative.
            </p>
            <button type="button" onClick={onClose} className="mt-6 px-4 py-2 rounded-lg bg-[#D4FF3A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#c2ed28] transition" data-id="element-284">
              Chiudi
            </button>
          </div>}
      </div>
    </div>;
}